import React, { useEffect, useState } from 'react'
import { Table, Empty, Spin, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { stockTakingService } from '@services/operator/stockTakingService'

/**
 * 展開某個 StockTakingItem 的盤點歷史（append-only count entries）。
 *
 * Lazy load: 只在元件 mount（即使用者展開該列）時才 fetch。
 *
 * Props:
 *   stockTakingId — string
 *   itemId        — string
 */
export default function CountEntriesTable({ stockTakingId, itemId }) {
  const { t } = useTranslation()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await stockTakingService.getItemCountEntries(stockTakingId, itemId)
        if (cancelled) return
        if (res.success) {
          setEntries(res.data?.item_count_entries || [])
        } else {
          message.error(res.message || t('common.error.load_failed'))
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Load count entries error:', err)
          message.error(t('common.error.unknown_error'))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [stockTakingId, itemId, t])

  if (loading) {
    return <div style={{ padding: 16, textAlign: 'center' }}><Spin size="small" /></div>
  }

  if (entries.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('shared.stock_takings.show.count_entries.empty')}
      />
    )
  }

  const formatDateTime = (s) => {
    if (!s) return '-'
    const d = new Date(s)
    return Number.isNaN(d.getTime())
      ? s
      : `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }

  const columns = [
    {
      title: t('shared.stock_takings.show.count_entries.counted_at'),
      dataIndex: 'counted_at',
      key: 'counted_at',
      width: 160,
      render: formatDateTime
    },
    {
      title: t('shared.stock_takings.show.count_entries.operator'),
      dataIndex: 'operator_name',
      key: 'operator_name',
      width: 160,
      render: (v) => v || '-'
    },
    {
      title: t('shared.stock_takings.show.count_entries.expected'),
      dataIndex: 'expected_quantity',
      key: 'expected_quantity',
      width: 96,
      align: 'right'
    },
    {
      title: t('shared.stock_takings.show.count_entries.counted'),
      dataIndex: 'counted_quantity',
      key: 'counted_quantity',
      width: 96,
      align: 'right'
    },
    {
      title: t('shared.stock_takings.show.count_entries.difference'),
      dataIndex: 'difference',
      key: 'difference',
      width: 96,
      align: 'right',
      render: (n) => {
        if (n > 0) return <span style={{ color: '#389e0d' }}>+{n}</span>
        if (n < 0) return <span style={{ color: '#cf1322' }}>{n}</span>
        return <span>{n}</span>
      }
    },
    {
      title: t('shared.stock_takings.show.count_entries.note'),
      dataIndex: 'note',
      key: 'note',
      render: (v) => v || '-'
    }
  ]

  return (
    <Table
      rowKey="id"
      size="small"
      dataSource={entries}
      columns={columns}
      pagination={false}
      style={{ margin: '0 16px' }}
    />
  )
}
